<script setup>
/**
 * FormGroup
 * Standard layout wrapper for form inputs. Manages labeling, help text,
 * character counters, and validation feedback. Includes logic to
 * handle "devOnly" field visibility based on the application's betaMode.
 */
import {computed} from 'vue'
import Tooltip from '@/common/Tooltip'
import FormControl from './FormControl'
import MaxLengthCounter from "@/vue/common/MaxLengthCounter.vue"
import {useFormStore} from '@/stores/form'
import {useAppStore} from '@/stores/appStore'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    hideHelpText: {
        type: Boolean,
        default: false
    }
})

const appStore = useAppStore()

const formStore = computed(() => props.formId ? useFormStore(props.formId) : null)
const currentData = computed(() => formStore.value?.currentData || {})
const betaMode = computed(() => appStore.betaMode)
const formSmall = computed(() => formStore.value?.formSmall)

props.field.label = props.field.label || props.field.title

if (props.field.devOnly && !betaMode.value) {
    props.field.hidden = true
}
</script>

<template>
    <div v-if="!field.hidden && !field.replaceFormgroup" v-show="field.visibility !== 'hidden'"
         :class="['n-' + field.name, field.class, {'small': formSmall}]"
         :data-type="field.type"
         data-form-group>

        <template v-if="field.type !== 'checkbox'">
            <label v-if="field.label" :for="field.id" class="form-label" :class="{'mb-1': field.helpText}">
                {{ field.label }}
            </label>
            <template v-if="!hideHelpText">
                <p v-if="field.helpText" v-html="field.helpText" class="form-text mt-0 mb-2"></p>
                <Tooltip v-else :hide="100" :show="250" :tooltip="field.helpText" :trigger="'hover'"/>
            </template>
        </template>

        <div class="has-validation">
            <div v-if="field.type === 'checkbox'" class="form-check">
                <FormControl :field="field" :form-id="formId"/>
                <label :for="field.id" class="form-check-label">{{ field.label }}</label>
                <template v-if="!hideHelpText">
                    <p v-if="field.helpText" v-html="field.helpText" class="form-text mt-0"></p>
                    <Tooltip v-else :hide="100" :show="250" :tooltip="field.helpText" :trigger="'hover'" class="ms-1"/>
                </template>
            </div>

            <FormControl v-else :field="field" :form-id="formId"/>

            <MaxLengthCounter
                v-if="field.maxlength && !field.readonly"
                :maxlength="field.maxlength"
                :value="currentData[field.name]"
            />

            <datalist v-if="field.datalist" :id="'datalist-' + field.id">
                <option v-for="value in field.datalist" :key="value" :value="value"></option>
            </datalist>

            <div v-if="field.error" class="invalid-feedback" v-html="field.error"/>
            <div v-else-if="field.required && !field.readonly && !currentData[field.name] && !field.error"
                 class="form-text">Required
            </div>
        </div>
    </div>

    <div v-else-if="field.replaceFormgroup">
        <FormControl :field="field" :form-id="formId"/>
    </div>
</template>

<style scoped>
.form-text {
    max-width: 600px;
}

/* Sizes match to prevent layout shift when toggling between help text and error messages */
.form-text,
.invalid-feedback {
    font-size: 13px;
}
</style>